import type { ReactNode } from 'react'
import { Tooltip } from '@structural-climbing/ui'

/**
 * Small hand-built charts for the Statistics page. Colours are a validated
 * set (CVD ΔE 12.4, normal-vision 29.3, see the dataviz checks): the gym
 * terracotta plus blue, aqua and violet. Aqua sits under 3:1 on white, so every
 * multi-series chart carries a legend and a tooltip naming each value.
 */
export const SERIES = {
  terracotta: '#BD4627',
  blue: '#2a78d6',
  aqua: '#1baf7a',
  violet: '#6250d6',
  soft: '#F3DCD3',
  faint: '#A1A1AA',
} as const

export interface Series {
  name: string
  color: string
  /** null leaves a gap: the line stops rather than inventing a value. */
  values: (number | null)[]
}

function TipBody({
  title,
  rows,
}: {
  title: string
  rows: { name?: string; color?: string; value: string }[]
}) {
  return (
    <div className="flex flex-col gap-0.5 px-1 py-0.5 text-xs">
      <span className="font-semibold">{title}</span>
      {rows.map((r, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {r.color && <span className="h-2 w-2 rounded-sm" style={{ background: r.color }} />}
          {r.name && <span className="text-foreground/70">{r.name}:</span>}
          <span className="font-semibold tabular-nums">{r.value}</span>
        </span>
      ))}
    </div>
  )
}

function Axis({ labels }: { labels: string[] }) {
  if (labels.length === 0) return null
  const mid = labels[Math.floor(labels.length / 2)]
  return (
    <div className="mt-1 flex justify-between text-[10.5px] tabular-nums text-foreground/40">
      <span>{labels[0]}</span>
      {labels.length > 2 && <span>{mid}</span>}
      <span>{labels[labels.length - 1]}</span>
    </div>
  )
}

export function Legend({ items }: { items: { name: string; color: string }[] }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11.5px] text-foreground/60">
      {items.map((i) => (
        <span key={i.name} className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: i.color }} />
          {i.name}
        </span>
      ))}
    </div>
  )
}

/** Lines over time, one point per label, a crosshair tooltip per column. */
export function TrendLine({
  series,
  labels,
  format = (v) => String(v),
  height = 130,
  fill = false,
  min,
}: {
  series: Series[]
  labels: string[]
  format?: (v: number) => string
  height?: number
  /** Shade under the first line. */
  fill?: boolean
  /** Bottom of the scale; defaults to just under the smallest value. */
  min?: number
}) {
  const n = labels.length
  const all = series.flatMap((s) => s.values).filter((v): v is number => v !== null)
  if (n === 0 || all.length === 0) return null
  const lo = min ?? Math.min(...all) * 0.95
  const hi = Math.max(...all) * 1.04 || 1
  const x = (i: number) => (n === 1 ? 50 : (i / (n - 1)) * 100)
  const y = (v: number) => 100 - ((v - lo) / (hi - lo || 1)) * 100
  const path = (values: (number | null)[]) => {
    let d = ''
    let pen = false
    values.forEach((v, i) => {
      if (v === null) {
        pen = false
        return
      }
      d += `${pen ? 'L' : 'M'}${x(i)},${y(v)} `
      pen = true
    })
    return d
  }
  const first = series[0]
  const lastIndex = (values: (number | null)[]) => {
    for (let i = values.length - 1; i >= 0; i--) if (values[i] !== null) return i
    return -1
  }

  return (
    <div>
      <div className="relative" style={{ height }}>
        {[0, 50, 100].map((p) => (
          <div
            key={p}
            className="absolute inset-x-0 border-t border-dashed border-default-200"
            style={{ top: `${p}%` }}
          />
        ))}
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full overflow-visible"
          aria-hidden="true"
        >
          {fill && (
            <path
              d={`M${x(0)},100 ${first.values.map((v, i) => (v === null ? '' : `L${x(i)},${y(v)}`)).join(' ')} L${x(lastIndex(first.values))},100Z`}
              fill={first.color}
              opacity={0.08}
            />
          )}
          {series.map((s) => (
            <path
              key={s.name}
              d={path(s.values)}
              fill="none"
              stroke={s.color}
              strokeWidth={2}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>
        {series.map((s) => {
          const i = lastIndex(s.values)
          if (i < 0) return null
          return (
            <span
              key={s.name}
              className="absolute h-2 w-2 rounded-full ring-2 ring-white"
              style={{
                background: s.color,
                left: `calc(${x(i)}% - 4px)`,
                top: `calc(${y(s.values[i] as number)}% - 4px)`,
              }}
            />
          )
        })}
        <div className="absolute inset-0 flex">
          {labels.map((label, i) => (
            <Tooltip
              key={label}
              closeDelay={0}
              content={
                <TipBody
                  title={label}
                  rows={series.map((s) => ({
                    name: series.length > 1 ? s.name : undefined,
                    color: series.length > 1 ? s.color : undefined,
                    value: s.values[i] === null ? '—' : format(s.values[i] as number),
                  }))}
                />
              }
            >
              <div className="group relative flex-1" aria-label={label}>
                <span className="absolute inset-y-0 left-1/2 hidden border-l border-foreground/25 group-hover:block" />
              </div>
            </Tooltip>
          ))}
        </div>
      </div>
      <Axis labels={labels} />
    </div>
  )
}

/** Columns, stacked when there is more than one value per label. */
export function Columns({
  rows,
  colors,
  names,
  format = (v) => String(v),
  height = 120,
  highlightMax = false,
  showAllLabels = false,
}: {
  rows: { label: string; values: number[] }[]
  colors: string[]
  names?: string[]
  format?: (v: number) => string
  height?: number
  /** One colour, the tallest column solid and the rest pale. */
  highlightMax?: boolean
  showAllLabels?: boolean
}) {
  const totals = rows.map((r) => r.values.reduce((a, b) => a + b, 0))
  const max = Math.max(0, ...totals)
  const peak = totals.indexOf(max)
  return (
    <div>
      <div className="flex items-end gap-[3px]" style={{ height }}>
        {rows.map((r, i) => (
          <Tooltip
            key={r.label}
            closeDelay={0}
            content={
              <TipBody
                title={r.label}
                rows={[
                  ...r.values.map((v, j) => ({
                    name: names && r.values.length > 1 ? names[j] : undefined,
                    color: r.values.length > 1 ? colors[j] : undefined,
                    value: format(v),
                  })),
                  ...(r.values.length > 1 ? [{ name: 'Σ', value: format(totals[i]) }] : []),
                ]}
              />
            }
          >
            <div
              className="flex h-full min-w-0 flex-1 flex-col-reverse gap-[2px]"
              aria-label={r.label}
            >
              {r.values.map((v, j) => (
                <span
                  key={j}
                  className={j === r.values.length - 1 || totals[i] === v ? 'rounded-t-[4px]' : ''}
                  style={{
                    height: max > 0 ? `calc(${(v / max) * 100}% - 2px)` : 0,
                    background: highlightMax ? (i === peak ? colors[0] : SERIES.soft) : colors[j],
                  }}
                />
              ))}
            </div>
          </Tooltip>
        ))}
      </div>
      {showAllLabels ? (
        <div className="mt-1 flex gap-[3px] text-[10.5px] text-foreground/45">
          {rows.map((r) => (
            <span key={r.label} className="min-w-0 flex-1 truncate text-center">
              {r.label}
            </span>
          ))}
        </div>
      ) : (
        <Axis labels={rows.map((r) => r.label)} />
      )}
    </div>
  )
}

/** Labelled horizontal bars, largest value full width. */
export function BarList({
  rows,
  format = (v) => String(v),
  color = SERIES.terracotta,
}: {
  rows: { label: string; value: number }[]
  format?: (v: number) => string
  color?: string
}) {
  const max = Math.max(0, ...rows.map((r) => r.value))
  return (
    <ul className="flex flex-col gap-2.5">
      {rows.map((r) => (
        <li
          key={r.label}
          className="grid grid-cols-[minmax(84px,32%)_1fr_auto] items-center gap-2 text-[12.5px]"
        >
          <span className="truncate">{r.label}</span>
          <span className="h-2.5 overflow-hidden rounded-full bg-default-100">
            <span
              className="block h-full rounded-full"
              style={{ width: max > 0 ? `${(r.value / max) * 100}%` : 0, background: color }}
            />
          </span>
          <span className="text-right font-semibold tabular-nums">{format(r.value)}</span>
        </li>
      ))}
    </ul>
  )
}

/** Weekday × hour, one hue from pale to dark. */
export function Heatmap({
  cells,
  weekdays,
  hours,
  format,
  quietLabel,
  busyLabel,
}: {
  cells: { weekday: number; hour: number; value: number }[]
  /** Labels for ISO weekdays 1..7. */
  weekdays: string[]
  hours: number[]
  format: (v: number) => string
  quietLabel: string
  busyLabel: string
}) {
  const max = Math.max(0, ...cells.map((c) => c.value))
  const at = (d: number, h: number) =>
    cells.find((c) => c.weekday === d && c.hour === h)?.value ?? 0
  return (
    <div className="flex flex-col gap-2">
      <div
        className="grid gap-[3px] text-[10px] text-foreground/40"
        style={{ gridTemplateColumns: `30px repeat(${hours.length}, minmax(0, 1fr))` }}
      >
        <span />
        {hours.map((h) => (
          <span key={h} className="text-center">
            {h % 2 === 0 ? h : ''}
          </span>
        ))}
        {weekdays.map((label, i) => (
          <HeatRow
            key={label}
            label={label}
            hours={hours}
            value={(h) => at(i + 1, h)}
            max={max}
            format={format}
          />
        ))}
      </div>
      <div className="flex items-center gap-2 text-[11.5px] text-foreground/55">
        <span>{quietLabel}</span>
        <span
          className="h-2 w-28 rounded-full"
          style={{ background: `linear-gradient(90deg, rgba(189,70,39,.07), rgba(189,70,39,.95))` }}
        />
        <span>{busyLabel}</span>
      </div>
    </div>
  )
}

function HeatRow({
  label,
  hours,
  value,
  max,
  format,
}: {
  label: string
  hours: number[]
  value: (h: number) => number
  max: number
  format: (v: number) => string
}) {
  return (
    <>
      <span className="self-center">{label}</span>
      {hours.map((h) => {
        const v = value(h)
        return (
          <Tooltip
            key={h}
            closeDelay={0}
            content={<TipBody title={`${label} ${h}:00`} rows={[{ value: format(v) }]} />}
          >
            <span
              aria-label={`${label} ${h}:00`}
              className="block h-4 rounded-[3px]"
              style={{
                background: `rgba(189,70,39,${(0.07 + 0.88 * (max > 0 ? v / max : 0)).toFixed(2)})`,
              }}
            />
          </Tooltip>
        )
      })}
    </>
  )
}

/** One bar split into parts, with a legend underneath. */
export function SplitBar({ parts }: { parts: { name: string; value: number; color: string }[] }) {
  const total = parts.reduce((a, p) => a + p.value, 0)
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex h-3 gap-[2px] overflow-hidden rounded-full">
        {parts.map((p) => (
          <span
            key={p.name}
            style={{ width: total > 0 ? `${(p.value / total) * 100}%` : 0, background: p.color }}
          />
        ))}
      </div>
      <Legend items={parts.map((p) => ({ name: `${p.name} ${p.value}`, color: p.color }))} />
    </div>
  )
}

export function Headline({
  value,
  note,
  tone = 'muted',
}: {
  value: ReactNode
  note?: ReactNode
  tone?: 'muted' | 'good' | 'bad'
}) {
  const toneClass =
    tone === 'good' ? 'text-success-600' : tone === 'bad' ? 'text-danger-600' : 'text-foreground/55'
  return (
    <p className="flex flex-wrap items-baseline gap-x-2">
      <span className="text-2xl font-bold tabular-nums tracking-tight">{value}</span>
      {note && <span className={`text-xs ${toneClass}`}>{note}</span>}
    </p>
  )
}
